<template>
  <section class="py-9 tablet:py-20">
    <div class="container-wrap">
      <div
        class="mb-10 desktop:flex"
        :class="
          descriptionHtml || description
            ? 'desktop:items-end'
            : 'desktop:items-center'
        "
      >
        <div class="desktop:flex-1">
          <h2 class="text-h2 mb-10 last:mb-0 tablet:line-clamp-2">
            {{ title }}
          </h2>
          <TheRichText
            v-if="descriptionHtml || description"
            class="w-full text-sm leading-6 text-gray-700 tablet:line-clamp-7 tablet:text-base"
            :text="descriptionHtml || description"
            :is-html="!!descriptionHtml"
          />
        </div>
        <div
          v-if="items.length > 1"
          class="hidden w-40 basis-40 pl-5 desktop:block"
        >
          <div
            class="inline-flex h-16 items-center overflow-hidden rounded-[64px] bg-gray-600_a15"
          >
            <button
              class="flex h-16 w-16 items-center justify-center rounded-full text-black"
              :class="
                isBeginning
                  ? 'pointer-events-none opacity-50'
                  : 'hover:bg-gray-600_a25 active:bg-gray-600_a35'
              "
              @click="prev()"
            >
              <IconCaret direction="left" />
            </button>

            <button
              class="flex h-16 w-16 items-center justify-center rounded-full text-black"
              :class="
                isEnd
                  ? 'pointer-events-none opacity-50'
                  : 'hover:bg-gray-600_a25 active:bg-gray-600_a35'
              "
              @click="next()"
            >
              <IconCaret direction="right" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <ClientOnly>
      <div>
        <swiper-container
          ref="containerRef"
          :slides-per-view="'auto'"
          :slides-per-group="1"
          :centered-slides="true"
          class="element-swiper-container"
        >
          <swiper-slide
            v-for="item in items"
            :key="item.name"
            class="swiper-slide h-auto p-2.5"
          >
            <PostcardQuote
              :title="item.name"
              :description="item.description"
              :button-url="item.link"
              :button-id="item.trackingCode"
              :image-url="item.thumbnail"
              :clickable="true"
              :has-shadow="isDesktop"
              :has-quote-icon="false"
              :theme="theme"
            />
          </swiper-slide>
        </swiper-container>
      </div>
    </ClientOnly>

    <!-- For SEO -->
    <ul class="hidden">
      <li v-for="item in items" :key="item.name">
        <PostcardQuote
          :title="item.name"
          :description="item.description"
          :button-url="item.link"
          :button-id="item.trackingCode"
          :image-url="item.thumbnail"
          :clickable="true"
          :has-shadow="isDesktop"
          :has-quote-icon="false"
          :theme="theme"
        />
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
  /**
   * @see https://www.figma.com/design/QJ3ZvEbUHI9xI11iudYf8U/Advantech-WISE-Library?node-id=12817-34204&t=89PIFDiQEC4zcSbf-0
   */

  interface IItem {
    name: string;
    description?: string;
    link: string;
    thumbnail: string;
    trackingCode?: string;
  }

  interface IProps {
    title: string;
    description?: string;
    descriptionHtml?: string;
    items: IItem[];
    theme?: TTheme;
  }

  const props = withDefaults(defineProps<IProps>(), {
    description: undefined,
    descriptionHtml: undefined,
    theme: 'blue',
  });

  const { isDesktop } = useScreen();

  const containerRef = ref<null>(null);

  const { instance, isBeginning, isEnd, next, prev } = useSwiper(containerRef, {
    loop: false,
    pagination: {
      enabled: true,
      clickable: true,
    },
  });

  watch(
    () => props.items,
    async () => {
      await nextTick();
      if (!instance.value) return;
      instance.value.updateSlides();
      instance.value.slideTo(0, 0);
      instance.value.updateSlidesClasses();
    }
  );

  export type { IItem };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/_utils.scss' as *;

  swiper-container {
    &::part(pagination) {
      @apply hidden;
    }
  }

  .element-swiper-container {
    .swiper-slide {
      width: 100%;
      transform: scale(0.9);
      opacity: 0.6;
      transition:
        transform 0.3s ease,
        opacity 0.3s ease;

      @include min-width($desktop-min-size) {
        width: 90%;
      }

      @include min-width(1496px) {
        width: 1320px;
      }
    }

    .swiper-slide-active {
      transform: scale(1);
      opacity: 1;
    }
  }
</style>
